import { deriveDescription, truncateAtWord } from "./seoText.js";

// The course page's metadata, built once and used twice: the prerender writes
// it into dist/courses/<slug>/index.html at build time, and <Helmet> renders the
// same tags at runtime.
//
// They have to agree exactly. The prerendered course tags carry data-rh, which
// hands them to react-helmet-async on hydration, and it leaves a tag alone only
// when the one the app renders is an identical node — anything that drifts gets
// removed and re-appended on every course page load. Deriving both from this
// module is what keeps them identical.

export const SITE_URL = "https://dekamas.com";
export const SITE_NAME = "Dekamas";
export const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;

export const TITLE_MAX = 60;
export const TITLE_SUFFIX = ` | ${SITE_NAME}`;

const QUALIFIERS = {
	bs: { course: " — online kurs", profile: " — certifikati" },
	en: { course: " — online course", profile: " — certificates" },
};

const OG_LOCALES = { bs: "bs_BA", en: "en_GB" };

// Every function defaults to Bosnian: the prerender calls them without a
// language, and its output must not change.
const qualifiers = (language) => QUALIFIERS[language] ?? QUALIFIERS.bs;

const DESCRIPTION_MAX = 155;

export function fitTitle(base, qualifier) {
	const full = `${base}${qualifier}${TITLE_SUFFIX}`;
	if (full.length <= TITLE_MAX) return full;

	const withoutQualifier = `${base}${TITLE_SUFFIX}`;
	if (withoutQualifier.length <= TITLE_MAX) return withoutQualifier;

	return `${truncateAtWord(base, TITLE_MAX - TITLE_SUFFIX.length)}${TITLE_SUFFIX}`;
}

/**
 * Field names are the prerender's camelCase catalogue shape rather than the
 * API's snake_case, because the caller on the app side has to pick fallbacks
 * anyway — a course whose detail endpoint 403s for anonymous visitors arrives
 * with no description and no module count.
 */
export function buildCourseSeo({
	slug,
	name,
	description,
	moduleCount,
	language = "bs",
}) {
	const derived = deriveDescription(description, DESCRIPTION_MAX);
	const fallback =
		language === "en"
			? `${name} — an online course in Bosnian with ${moduleCount} module${moduleCount === 1 ? "" : "s"}.`
			: `${name} — online kurs na bosanskom jeziku sa ${moduleCount} cjelina.`;

	return {
		title: fitTitle(name, qualifiers(language).course),
		description: derived || truncateAtWord(fallback, DESCRIPTION_MAX),
		canonical: `${SITE_URL}/courses/${slug}`,
	};
}

/**
 * A profile's metadata, derived once for both sides exactly as the course
 * metadata is: scripts/prerender/render-head.mjs writes these tags into the
 * static HTML and <Helmet> on UserProfile renders them at runtime.
 *
 * The holder's name leads the title and the username stands in when the
 * profile carries no name, so the page is still identifiable either way.
 */
export function buildProfileSeo({
	username,
	firstName,
	lastName,
	certificateCount,
	language = "bs",
}) {
	const name = `${firstName ?? ""} ${lastName ?? ""}`.trim() || username;
	const count = certificateCount ?? 0;
	const sentence =
		language === "en"
			? `${name} has completed ${count} course${count === 1 ? "" : "s"} on Dekamas. See their certificates.`
			: `${name} je završio/la ${count} kurseva na Dekamasu. Pogledajte certifikate.`;

	return {
		title: fitTitle(name, qualifiers(language).profile),
		description: truncateAtWord(sentence, DESCRIPTION_MAX),
		canonical: `${SITE_URL}/u/${username}`,
	};
}

/**
 * The meta tags both sides emit, in one order, so the prerendered markup and
 * the app's <Helmet> children line up tag for tag.
 *
 * og:image and twitter:image are deliberately absent: the cover image lives on
 * the /courses/all/ summary, which the course page never fetches, so only the
 * prerender can fill them in. They stay unmarked in the built HTML and Helmet
 * leaves them where they are.
 */
export function courseMetaTags({
	title,
	description,
	canonical,
	language = "bs",
}) {
	return [
		{ name: "description", content: description },
		{ property: "og:type", content: "website" },
		{ property: "og:site_name", content: SITE_NAME },
		{ property: "og:title", content: title },
		{ property: "og:description", content: description },
		{ property: "og:url", content: canonical },
		{ property: "og:locale", content: OG_LOCALES[language] ?? OG_LOCALES.bs },
		{ name: "twitter:card", content: "summary_large_image" },
		{ name: "twitter:title", content: title },
		{ name: "twitter:description", content: description },
	];
}
